import { NotePencilIcon, PlusIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { ActionBarItem } from '@/components';
import {
    PopoverRoot,
    PopoverContent,
    PopoverTrigger,
} from '@/components/Popover';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import type { ReportEventThumbnailFrame } from '@/types/report';
import { ManageThumbnailsList } from './ManageThumbnailsList';

interface ManageReportEventThumbnailsPopoverProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    sourceId: string;
    thumbnails: ReportEventThumbnailFrame[];
    addThumbnailDisabled: boolean;
    onThumbnailsChange: (next: ReportEventThumbnailFrame[]) => void;
    onStartAddThumbnail: () => void;
}

export function ManageReportEventThumbnailsPopover({
    open,
    onOpenChange,
    sourceId,
    thumbnails,
    addThumbnailDisabled,
    onThumbnailsChange,
    onStartAddThumbnail,
}: ManageReportEventThumbnailsPopoverProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    const addThumbnailAction = (
        <ActionBarItem
            icon={PlusIcon}
            label={t('eventEditor.addThumbnail')}
            onClick={onStartAddThumbnail}
            disabled={addThumbnailDisabled}
            className="w-full"
        />
    );

    return (
        <>
            <div
                aria-hidden
                className={cn(
                    'pointer-events-none absolute inset-0 z-[1] rounded-md bg-grey-700',
                    'transition-opacity duration-150 ease-out',
                    'opacity-0 group-hover:opacity-70',
                    open && 'opacity-70',
                )}
            />
            <PopoverRoot open={open} onOpenChange={onOpenChange}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'absolute inset-0 z-[2] flex cursor-pointer items-center justify-center rounded-md',
                            'bg-transparent opacity-0 transition-opacity duration-150 ease-out',
                            'group-hover:opacity-100 focus-visible:opacity-100',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white-100 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
                            open && 'opacity-100',
                        )}
                        aria-label={t('eventEditor.manageThumbnailsAria')}
                    >
                        <NotePencilIcon
                            className="pointer-events-none h-6 w-6 text-white-100"
                            weight="regular"
                            aria-hidden
                        />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    side="right"
                    align="start"
                    sideOffset={8}
                    collisionPadding={16}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    className="max-h-[360px] w-[236px] gap-0 overflow-hidden p-0"
                >
                    <div className="w-full shrink-0 px-4 pb-2 pt-4">
                        <p className="truncate text-sm font-medium text-white-100">
                            {t('eventEditor.manageThumbnailsPopoverTitle')}
                        </p>
                    </div>
                    <div
                        className="overflow-auto overscroll-contain px-3 pb-3 touch-pan-y"
                        onWheel={(wheelEvent) => wheelEvent.stopPropagation()}
                    >
                        <ManageThumbnailsList
                            sourceId={sourceId}
                            thumbnails={thumbnails}
                            onThumbnailsChange={onThumbnailsChange}
                        />
                    </div>
                    <div className="w-full shrink-0">
                        <div className="h-px w-full bg-grey-500" aria-hidden />
                        <div className="w-full px-4 py-3">
                            {addThumbnailDisabled ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="block w-full">
                                            {addThumbnailAction}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" sideOffset={8}>
                                        <p className="max-w-xs">
                                            {t(
                                                'eventEditor.addThumbnailUnavailableCamera',
                                            )}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            ) : (
                                addThumbnailAction
                            )}
                        </div>
                    </div>
                </PopoverContent>
            </PopoverRoot>
        </>
    );
}
